<script setup>
	import { ref, onMounted, inject, nextTick} from 'vue'
	import { useRoute, useRouter } from 'vue-router'
	import { event } from 'vue-gtag'

	import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';

	//import TopicSelectionModal from "@/components/explorer/options/TopicSelectionModal.vue"
	import ExplorerFilterPanel from "@/components/explorer/filter/FilterPanel.vue"
	import VisController from "@/components/explorer/VisController.vue";
	import VisMenu from "@/components/explorer/VisMenu.vue"
	import {validateRequestProps as validateLineRequestProps}  from './visualizations/line/LineChartSetup';
	import {validateRequestProps as validateBarChartRequestProps}  from './visualizations/bar/BarChartSetup';
	import {validateRequestProps as validateTreemapChartRequestProps}  from './visualizations/treemap/TreemapChartSetup';
	import {validateRequestProps as validateSankeyChartRequestProps}  from './visualizations/sankey/SankeyChartSetup';
	import {validateRequestProps as validateTableRequestProps}  from './visualizations/table/GridTableSetup';
	import {validateRequestProps as validateMapRequestProps}  from './visualizations/map/MapSetup';
	import ExplorerOptions from "@/components/explorer/options/ExplorerOptions.vue";
	import {ChartTypeEnum, getchartNameFromApiName, getApiNameFromChartName} from  "@/components/explorer/visualizations/ChartTypeEnum.js"
	import BannerMsg from "@/components/common/BannerInfo.vue"
	import ToastMsg from "@/components/common/ToastInfo.vue"
	//import DropDown from "@/components/common/DropDownSelect.vue"
	import HomeSearch from "@/components/home/HomeSearch.vue";
	/*import "@/components/accessibility/accessibility.js";*/
	import * as Accessibility from "@/components/accessibility/accessibility.js";



	//componenet methods exposed to parent
	defineExpose({
		updateUrlParms,
		openFiltersPanel,
		refreshFilter
		//showNoDataMsg
	})

	const version = __APP_VERSION__

	//reactive field proxies
	const selectedIndicatorName = ref('')

	const showSettings = ref(false);
	const selectedChartType = ref({})
	const validChartTypes = ref([])
	const toastMsg = ref('')
	const toastType = ref('')
	const showToast = ref(false)
	const bannerMsg = ref('')
	const bannerType = ref('')
	const btnLabel = ref('')
	const showBanner = ref(false)	
	const chartObj = ref({})

	//binding to invoke method on child componenet
	const childRefVisController = ref({})
	const childRefExplorerFilterPanel = ref({})
	const showNoDataWarning = ref(false)

	//local variables
	const route = useRoute()
	const router = useRouter()
	let indicator = {}
	let chartSettings = {};
	let decimalPrecision = [] 
	//let showSkeletonLoader = false

	const fontsize = Accessibility.readUserPreference('fontsize');
	const fontsizechart = Accessibility.readUserPreference('fontsize-chart');
	const colorblind = Accessibility.readUserPreference('colorblind');
	const patternfill = Accessibility.readUserPreference('patternfill');

	const apiUrl = import.meta.env.VITE_API_URL;
	//console.log('api', apiUrl);

	const { explorerState, updateExplorerState } = inject('explorerState') //holds the url of last visualization (indicator + options + filters)
	const { userInputState } = inject('userInputState')

	//Methods
 	function openFiltersPanel(isAreaSelected) {
		//if called by user clicking a map area, set filter to selected area when filter opens
		if(isAreaSelected) {
		 	childRefExplorerFilterPanel.value.initFilters(selectedChartType.value, indicator, chartSettings)
		} else {
			//toggle filter panel display
			showSettings.value = !showSettings.value
			childRefExplorerFilterPanel.value.initFilters(selectedChartType.value, indicator, chartSettings)
		}
	}	

	const closeFilters = () => {
		showSettings.value = false;
	}

	async function getDecimalPrecision() {
		const file = import.meta.env.VITE_BASE_URL + "/json/decimalPrecision.json"
		try{
			let x = await fetch(file);
			let y = await x.text();
			decimalPrecision = JSON.parse(y);
		}catch(e){
			console.log('file not found: '+ file)
		}
	}

	async function setupDefaults() {
		//Wait for visCntrl to be mounted before calling method
		await nextTick()
		await childRefVisController.value.toggleSkeletonLoader(true)
		try {
			await getDecimalPrecision() 
			await updateIndicator(import.meta.env.VITE_API_INDICATOR)	
		} catch (err) {
			console.log( 'Error loading topic',err)
			//showSkeletonLoader = false
			let msg = 'Error loading topic'
			//Failed to Fetch indicates API network error, show user contact info
			if(err.message && err.message == 'Failed to fetch')
				msg = 'Error loading topic, API error'
			showHomePageError(msg)
			return;	
		} finally {
			await childRefVisController.value.toggleSkeletonLoader(false)
		}
	}

	async function loadChartByUrl(query) {

		try {
			//showSkeletonLoader = true
			await nextTick()			
			await childRefVisController.value.toggleSkeletonLoader(true)
			await getDecimalPrecision() 			
			const decompressed = decompressFromEncodedURIComponent(query.o);
			const obj = JSON.parse(decompressed);
			//console.log("Decompressed params:", obj);
			//const jsonString = JSON.stringify(obj);
			//console.log('Json str', jsonString.length);
			//const byteSize = new TextEncoder().encode(jsonString).length;
			//console.log(`Byte size: ${byteSize} bytes`);
			//console.log('indicator', indicator)

			// Add version field to OR condition
			if(!obj.request.datasetName)
				throw new  Error('Invalid chart defined in URL');			

			//temporarially disable watcher on indicator name to set property required by setIndicatorName()
			//stopWatcher()

			let deprecatedValue = false;
			let missingDimension = false;
			let additionalValueAvailable = false;	
			try {
				clearError()
				selectedIndicatorName.value = obj.request.datasetName				
				await setIndicatorDetails(selectedIndicatorName.value, obj.type)
				selectedChartType.value = obj.type
				//console.log('indicator', indicator)
				userInputState.value.year = false;
				userInputState.value.geo = false;

				event('select_indicator', { 
					'indicator' : selectedIndicatorName.value.trim(), 
					'colorblind' :  colorblind?colorblind:'default',
					'patternfill' : patternfill?patternfill:false,
					'fontsize' : (fontsize && fontsize.length)?fontsize+'%':'100%',
					'fontsizechart' : (fontsizechart && fontsizechart.length)?fontsizechart+'%':'100%'
				})

				//validate URL query contains all required fields found in indicator using metadata version field
				if(selectedChartType.value == ChartTypeEnum.BARH.chartName ||
					selectedChartType.value == ChartTypeEnum.BAR.chartName) {
					let retObj = validateBarChartRequestProps(obj, indicator)
					missingDimension = retObj.missingDimension
					deprecatedValue = retObj.deprecatedValue	
				} else if(selectedChartType.value == ChartTypeEnum.TREEMAP.chartName) {
					let retObj = validateTreemapChartRequestProps(obj, indicator)
					missingDimension = retObj.missingDimension
					deprecatedValue = retObj.deprecatedValue
				} else if(selectedChartType.value == ChartTypeEnum.SANKEY.chartName) {
					let retObj = validateSankeyChartRequestProps(obj, indicator)
					missingDimension = retObj.missingDimension
					deprecatedValue = retObj.deprecatedValue
				} else if(selectedChartType.value == ChartTypeEnum.LINE.chartName) {
					let retObj = validateLineRequestProps(obj, indicator)
					missingDimension = retObj.missingDimension
					deprecatedValue = retObj.deprecatedValue
					additionalValueAvailable = retObj.additionalValueAvailable
				} else if(selectedChartType.value == ChartTypeEnum.TABLE.chartName) {
					let retObj = validateTableRequestProps(obj, indicator)
					missingDimension = retObj.missingDimension
					deprecatedValue = retObj.deprecatedValue
				} else if(selectedChartType.value == ChartTypeEnum.MAP.chartName) {
					let retObj = validateMapRequestProps(obj, indicator)
					missingDimension = retObj.missingDimension
					deprecatedValue = retObj.deprecatedValue
				}						
				//build chart
				chartSettings = await childRefVisController.value.setupVis(selectedChartType.value, indicator, null, obj)
				if(deprecatedValue || missingDimension) {
					//showPageWarning("Some selections in your request are no longer available.")
					showPageInfo("Some data for this indicator have been updated. Please reference the Updates page to view changes.", "Updates")
				} else if(additionalValueAvailable) {
					showPageInfo("You can see more years of data for this indicator by updating your year selection in Settings.")
				}

				//if visualization page loaded ater returning from other parts of the tool, check if query parameters need to be reset
				const urlQuery = { ...route.query }
				if(urlQuery.o ==null) {
					router.replace({ query })
				}
				updateExplorerState({ query })
				return
			} catch (err) {
				//showSkeletonLoader = false
				console.log( 'loadChartByUrl',err)
				showHomePageError(`The URL was not recognized. If you believe this to be an error, please contact us at <a href='mailto:${import.meta.env.VITE_API_MAILTO}'>${import.meta.env.VITE_API_MAILTO}</a>.`)
				return;			
			}
		} catch (err) {
			//showSkeletonLoader = false
			console.log( 'loadChartByUrl',err)
			showHomePageError(`The URL was not recognized. If you believe this to be an error, please contact us at <a href='mailto:${import.meta.env.VITE_API_MAILTO}'>${import.meta.env.VITE_API_MAILTO}</a>.`)
			return;			
		} finally {
			await childRefVisController.value.toggleSkeletonLoader(false)
		}
	}

	//TODO: Remove timings
	function getTime() {
		var d = new Date();
		var s = d.getSeconds();
		var mil = d.getMilliseconds();
		var h = d.getHours();
		var m = d.getMinutes();
		return h+':'+m+':'+s+':'+mil;
	}
	
	async function loadChartByIndicatorName(indName) {

		try {
			//showSkeletonLoader = true
			await nextTick()
			await childRefVisController.value.toggleSkeletonLoader(true)
			if(import.meta.env.VITE_APP_DEBUG)
				console.log('START INDICATOR META RETRIEVAL', getTime())
			await getDecimalPrecision()
	
			if(import.meta.env.VITE_APP_DEBUG)
				console.log('START INDICATOR SET DETAILS', getTime())
			await updateIndicator(indName, null)
			if(import.meta.env.VITE_APP_DEBUG)
				console.log('END INDICATOR DETAILS', getTime())
		} catch (err) {
			//showSkeletonLoader = false
			console.log( 'loadChartByIndicatorName',err)
			let msg = 'Error loading topic'
			//Failed to Fetch indicates API network error, show user contact info
			if(err.message && err.message == 'Failed to fetch')
				msg = 'Error loading topic, API error'
			showHomePageError(msg)
			return;	
		} finally {
			await childRefVisController.value.toggleSkeletonLoader(false)
		}
	}

 	async function getIndicatorMeta(indName) {
		const url = apiUrl + '/indicators/api/metadata/' + encodeURIComponent(indName.trim())
		const response = await fetch(url, {
			method: 'GET',
			headers: {'Content-Type': 'application/json', 'x-api-version': version}
		})
		if (!response.ok) {
			throw new Error(`HTTP status: ${response.status}`);
		}
		const data = await response.json();
		return data;
	}		
	
	async function setIndicatorDetails(indName, chartType) {
		indicator = await getIndicatorMeta(indName)
		if(import.meta.env.VITE_APP_DEBUG)
			console.log('indicator', indicator)
		indicator.decimalPrecision = decimalPrecision[indName]?.precision||0
		if(chartType) {
			indicator.defaultApiChartType = getApiNameFromChartName(chartType)
			selectedChartType.value = chartType
		} else {
			//default chart type to 1st chart type
			indicator.defaultApiChartType = indicator.chartTypes[0]
			selectedChartType.value = getchartNameFromApiName(indicator.defaultApiChartType)
		}
		validChartTypes.value = indicator.chartTypes;
		if(indicator == null)
			return false
		else
			return true
	}	

	async function updateChartType (type) {
		clearError()
		selectedChartType.value = type
		event('update_charttype', { 'chart_type': type });
		try {
			chartSettings = await childRefVisController.value.updateChartType(selectedChartType.value, indicator, chartSettings)
			if(type == ChartTypeEnum.SOURCE.chartName){
				closeFilters()
			} else {
				updateUrlParms();			
				if(showSettings.value) {
					//refresh filter list
					childRefExplorerFilterPanel.value.closeFilterItem()
					childRefExplorerFilterPanel.value.initFilters(selectedChartType.value, indicator, chartSettings)
				}
			}
		} catch (err) {
			console.log('Error updateChartType', err) 
			showHomePageError( "Error updating chart type")
			return;						
		}
	}

	async function updateIndicator(ind, chartType) {
		clearError()
		selectedIndicatorName.value = ind
		//updateUserInputState( false );
		userInputState.value.year = false;
		userInputState.value.geo = false;
		event('select_indicator', { 
					'indicator' : ind.trim(), 
					'colorblind' :  colorblind?colorblind:'default',
					'patternfill' : patternfill?patternfill:false,
					'fontsize' : (fontsize && fontsize.length)?fontsize+'%':'100%',
					'fontsizechart' : (fontsizechart && fontsizechart.length)?fontsizechart+'%':'100%'
				})
		if(await setIndicatorDetails(selectedIndicatorName.value, chartType)) {
			try {
				chartSettings = await childRefVisController.value.setupVis(selectedChartType.value, indicator)
				updateUrlParms()
				//Refresh filter panel with current category if open
				if(showSettings.value) {
					childRefExplorerFilterPanel.value.closeFilterItem()	
					childRefExplorerFilterPanel.value.initFilters(selectedChartType.value, indicator, chartSettings)
				} 
				return true
			} catch {	
				//throw new Error("Error loading indicator", { cause: err })	
				showHomePageError( "Error changing indicator")
				return;									
			}
		} else {
			//throw new Error("Error loading indicator");
			showHomePageError( "Error loading indicator")
			return;					
		}
	}

	function clearError() {
		//hide error if shown
		toastMsg.value = ""
		toastType.value = ""
		showToast.value = false;
	}

	function refreshFilter(settings) {
		chartSettings =  settings
		selectedChartType.value = settings.type
		updateUrlParms();
		if(showSettings.value) {
			//refresh filter list
			childRefExplorerFilterPanel.value.closeFilterItem()
			childRefExplorerFilterPanel.value.initFilters(selectedChartType.value, indicator, settings)
		}
	}

	async function applyFilter(dim) {
		clearError()
		
		try {
			await childRefVisController.value.applyFilter(indicator, dim)
			updateUrlParms();
		} catch (err) {
			console.log( 'Error applyFilter',err)
			showHomePageError( "Error applying filter")
			return;						
		}
	}

	async function resetFilters() {
		try {
			await updateIndicator(indicator.indicatorName, selectedChartType.value)	
		} catch (err) {
			console.log( 'Error loading topic',err)
			let msg = 'Error loading topic'
			//Failed to Fetch indicates API network error, show user contact info
			if(err.message && err.message == 'Failed to fetch')
				msg = 'Error loading topic, API error'
			showHomePageError(msg)
			return;	
		}
	}

	function showPageInfo(msg) {
		toastMsg.value = msg.message?msg.message:msg
		toastType.value = 'info'
		event('info_warning', { 'message' : toastMsg.value })
		showToast.value = true;	
	}	

	function showHomePageError(msg) {
		const msgObj = {
			"text": msg.message?msg.message:msg,
			"type": "error"
		}
		//router.push({ name: 'HomeView' })
 		router.push({
			name: 'HomeView',
			//path: "/",
			state: { "msgObj": msgObj } // stored in history.state, not URL
		}) 
	}

	function setupChartObj(obj) {
		chartObj.value = obj;
	}

	function setupMapObj(obj) {
		chartObj.value = obj;
	}

	function updateUrlParms() {
		const query = { ...route.query }

		if(chartSettings == null || chartSettings.request == null)
			return

		//override req series with selection state from chart settings
		if(chartSettings.type == ChartTypeEnum.BAR.chartName ||
			chartSettings.type == ChartTypeEnum.BARS.chartName ||
			chartSettings.type == ChartTypeEnum.BARH.chartName ||
			chartSettings.type == ChartTypeEnum.BARHS.chartName ||	
			chartSettings.type == ChartTypeEnum.LINE.chartName) {
			let seriesVals = chartSettings.seriesDim.appliedValues.filter(x => x.selected).map(x => x.name)
			let seriesDim
			if(chartSettings.request.categories)
				seriesDim = chartSettings.request.categories.find(x => x.categoryName ==chartSettings.seriesDim.columnName)
			else
				seriesDim = chartSettings.request.columns.find(x => x.columnName ==chartSettings.seriesDim.columnName)

			seriesDim.filters = seriesVals
		}
		const obj = {
			"type": selectedChartType.value,
			//"topic": selectedTopicName.value,
			//"subtopic": selectedSubTopicName.value,
			"request": chartSettings.request
		}
		if(chartSettings.toggleHorizontalBar)
			obj.toggleHorizontalBar = chartSettings.toggleHorizontalBar
		if(chartSettings.toggleStack)
			obj.toggleStack = chartSettings.toggleStack		

		//console.log('URL obj', obj)
		let jsonStr = JSON.stringify(obj)
		//console.log('o', jsonStr)
		//console.log('URL uncompressed', jsonStr.length)
		
		//let uriComp = encodeURIComponent(jsonStr);
		//console.log('encodeURI',uriComp)
		//console.log('encodeURI', uriComp.length)

		const compressed1 = compressToEncodedURIComponent(jsonStr);
		//console.log('URL compressed', compressed1.length)
		query.o = compressed1
		
		updateExplorerState({ query })
		router.replace({ query })
	}

	//initial method run on page load
	const query = { ...route.query }
	if(query.o !=null) {
		loadChartByUrl(query)
	} else if(query.iname !=null) {
		loadChartByIndicatorName(decodeURIComponent(query.iname))
	} else if(explorerState.value !=null) {
		loadChartByUrl(explorerState.value.query)
	} else
		setupDefaults() 
	
	onMounted(() => {
		// Digital Analytics Program (https://digital.gov/guides/dap) 
        if(import.meta.env.VITE_APP_ENV && import.meta.env.VITE_APP_ENV === 'production') {
            const dapScript = document.createElement("script");
                dapScript.setAttribute("async", "")
                dapScript.setAttribute("id","_fed_an_ua_tag");
                dapScript.setAttribute("type","text/javascript");
                dapScript.setAttribute("src","https://dap.digitalgov.gov/Universal-Federated-Analytics-Min.js?agency=NSF");
            document.head.appendChild(dapScript);
        }
	})
</script>

<template>
	<div class="msg-container">
		<BannerMsg :show="showBanner" :type="bannerType" :btnLabel="btnLabel" :msgContent="bannerMsg"></BannerMsg>
		<ToastMsg :show="showToast" :type="toastType" :msgContent="toastMsg"></ToastMsg>
	</div>
	<div class="explorer-container">
		<div class="chart-container" :class="{'show-settings' : showSettings}">
			<div class="chart-setup">
				<div class="chart-setup-row">
					<div class="search-box-container">
						<HomeSearch :indName="indicator.indicatorName"/>
					</div>
					<div>
						<ExplorerOptions v-model="showNoDataWarning" :chartObj="chartObj" :indicator="indicator" :chartType="selectedChartType.toString()" />
					</div>
				</div>
				<div class="chart-setup-row" style="margin-top: 1.31rem">
					<VisMenu :chartType="selectedChartType.toString()" :validChartTypes="validChartTypes" @updateChart="updateChartType" />
				</div>
			</div>
			<div class="flexible">
				<VisController v-model="showNoDataWarning" ref="childRefVisController" @setChartObj="setupChartObj" @setMapObj="setupMapObj" @raiseError="showHomePageError" @clearError="clearError" @refreshFilter="refreshFilter" @openFiltersPanel="openFiltersPanel" />
			</div>			
		</div>
		<div class="settings-container" :class="{'show' : showSettings}">
			<ExplorerFilterPanel ref="childRefExplorerFilterPanel" @closeFilterPanel="closeFilters" @applyFilter="applyFilter" @resetFilters="resetFilters" />
		</div>
	</div>
</template>

<style scoped>
.msg-container {
	/*margin-bottom: 1.25rem;*/
	width: 100%;	
}
.msg-container:empty {
  display: none;
}

.explorer-container {
	width: 100%;
	display: flex;
	flex-wrap: nowrap;
	justify-content: center;
	background-color: var(--surface-color-Secondary, #F3F5F7);
	gap: 1.25rem;
	
	flex-grow: 1;
	flex-shrink: 1;
	flex-direction: row;
}

.search-box-container {
	width: 100%;
	max-width: 38.5625rem;
}

.chart-container {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	flex-shrink: 1;
	min-width: 375px;
	width: 100%;
 	min-height: 0;     /* CRUCIAL: Allows the container to shrink and scale down */
  	position: relative; /* Encourages proper layout context calculation */
	transition: max-width .2s linear;
	
	border-radius: 0.75rem;
	background-color: var(--surface-color-Default, #fff);
	box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.10);

    line-height: 1.5;
    background-color: var(--surface-color-Default, #fff);
}

.chart-container.show-settings{
	/*max-width: 73.75rem; /* 1180px */
}

.flexible {
	display: flex;
	flex-direction: column;
	height: 100%; /* Or fixed height */
	min-width: 0;
	/*display: flex;
	flex-direction: column;
	flex: 1 1 0%;
	min-width: 0;
	min-height: 0;      /* CRUCIAL: Allows the container to shrink and scale down */
  	position: relative; /* Encourages proper layout context calculation */
	overflow: hidden;  /* Collapses constraints, forcing Highcharts to adapt */
}

.chart-setup-row {
	min-height: 2.5rem;
	display: flex;
	justify-content:space-between;
	align-items: center;
	max-width: 100%;
}

.chart-setup {
	line-height: 1.5;
	background-color: var(--surface-color-Default, #fff);
	margin: .75rem 1.25rem;
}

.settings-container {
	display: none;
}

.settings-container.show {
	display: flex;
}	

.topic-label {
	color: var(--text-Secondary, #757575);
	white-space: nowrap;     
	overflow: hidden;
	text-overflow: ellipsis;	

	/* Medium/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 500;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */
}

.readonly-input {
	display: inline-flex;
	height: 2.5rem;
	padding: 0.5rem 2.25rem 0.5rem 0.75rem;
	align-items: center;
	flex-shrink: 0;
	min-width: 17.75rem;

	border-radius: 0.25rem;
	border: 1px solid var(--borders-input-Border, #A9B7C7);
	background: var(--input-background-Default, #FFF);	

	overflow: hidden;
	color: var(--text-text-primary, #2C2C2C);
	text-overflow: ellipsis;

	/* Regular/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */	
}
 
.btn-tertiary {
	display: inline-block;
	padding: 0 0.5rem;
	height: 2.5rem;
	min-width: 6.25rem;
} 

button.arrow {
	margin-left: 1rem;
	height: 2.5rem;
	min-width: 5rem;
	min-height: 2.5rem;
	color: var(--text-Link);
	background-color: var(--button-tertiary-Default);
	border: 0;

	/* Medium/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 500;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */	
}

.icon-settings {
	color: var(--icon-cta-Blue);
	margin-right:.5rem;
}

.change-topic {
	display: inline-flex;
	align-items: center;
	gap: var(--spacing-lg, 16px);
	max-width: calc(100% - 25rem);
}

#catList {
	min-width:17.75rem;
}

/* 499px - Mobile */
@media (max-width: 31.1875rem) {
	.chart-container {
		border-radius: 0;
	}
}

/* 1023px - iPad */
@media (max-width: 63.9375rem) {
	.explorer-container {
		flex-wrap: wrap;
	} 

	.hide-element {
		display:none;
	}

	.change-topic {
		max-width: calc(100% - 4rem);
	}	
}
</style>